import { Component, ViewChild } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { SecuredBaseComponent } from '../base/secured-base.component';
import { FilterPeriodsComponent } from '../filter-periods/filter-periods.component';
import { PaginatedList } from '../models/PaginatedList';
import { Pagination } from '../models/Pagination';
import { PaymentMethod } from '../models/PaymentMethod';
import { PeriodFilter } from '../models/PeriodFilter';
import { Transaction } from '../models/Transaction';
import { TransactionDirection } from '../models/TransactionDirection';
import { TransactionFilter } from '../models/TransactionFilter';
import { PaymentMethodService } from '../services/payment-method.service';
import { SearchFilterBuilderService } from '../services/search-filter-builder.service';
import { TransactionService } from '../services/transaction.service';
import { formatDate } from '@angular/common';
import { ManagerService } from '../services/manager.service';

@Component({
  selector: 'transactions',
  templateUrl: 'transactions.component.html',
  styleUrls: ['transactions.component.css']
})
export class TransactionsComponent extends SecuredBaseComponent {

  @ViewChild(FilterPeriodsComponent) filterPeriods!: FilterPeriodsComponent;

  constructor(public override snackBar: MatSnackBar, public override router: Router, public override managerService: ManagerService,
    private transactionService: TransactionService, private searchFilterBuilderService: SearchFilterBuilderService,
    public paymentMethodService: PaymentMethodService)
  {
    super(snackBar, router, managerService);
  }

  transactions: PaginatedList<Transaction> = {
    totalCount: 0,
    pageSize: environment.pageSize,
    from: 0,
    entities: [],
    currentPage: 1,
    totalPages: 1,
    to: 0,
    hasPreviousPage: false,
    hasNextPage: false
  };

  filterFields = [
    { id: '', name: 'All'},
    { id: 'transaction_id', name: 'Transaction ID' },
    { id: 'identifier', name: 'Wallet Identifier' },
    { id: 'address_name', name: 'Wallet Name' },
    { id: 'id', name: 'Recipient or Sender Identifier' },
    { id: 'name', name: 'Recipient or Sender Name' },
    { id: 'amount', name: 'Amount' },
    { id: 'unit', name: 'Unit' },
    { id: 'country', name: 'Country' },
    { id: 'reference_id', name: 'Reference ID' },
    { id: 'payment_request_id', name: 'Payment Request Identifier' },
    { id: 'cc_number', name: 'Card last 4 digits' },
    { id: 'payment_id', name: 'Provider Payment ID' }
  ];

  filterDirections = [
    { id: TransactionDirection.none, name: 'All'},
    { id: TransactionDirection.income, name: 'Income'},
    { id: TransactionDirection.outcome, name: 'Outcome'}
  ];

  filterPaymentMethods = [
    { id: PaymentMethod.none, name: 'All'},
    { id: PaymentMethod.usdtErc20, name: this.paymentMethodService.nameof(PaymentMethod.usdtErc20)},
    { id: PaymentMethod.usdtTrc20, name: this.paymentMethodService.nameof(PaymentMethod.usdtTrc20)},
    { id: PaymentMethod.usdcErc20, name: this.paymentMethodService.nameof(PaymentMethod.usdcErc20)},
    { id: PaymentMethod.googlePay, name: this.paymentMethodService.nameof(PaymentMethod.googlePay)},
    { id: PaymentMethod.applePay, name: this.paymentMethodService.nameof(PaymentMethod.applePay)},
    { id: PaymentMethod.monero, name: this.paymentMethodService.nameof(PaymentMethod.monero)},
    { id: PaymentMethod.openBanking, name: this.paymentMethodService.nameof(PaymentMethod.openBanking)},
    { id: PaymentMethod.creditCard, name: this.paymentMethodService.nameof(PaymentMethod.creditCard)},
    { id: PaymentMethod.tron, name: this.paymentMethodService.nameof(PaymentMethod.tron)},
    { id: PaymentMethod.ethereum, name: this.paymentMethodService.nameof(PaymentMethod.ethereum)},
    { id: PaymentMethod.bitcoin, name: this.paymentMethodService.nameof(PaymentMethod.bitcoin)},
    { id: PaymentMethod.payPal, name: this.paymentMethodService.nameof(PaymentMethod.payPal)},
    { id: PaymentMethod.cardMember, name: this.paymentMethodService.nameof(PaymentMethod.cardMember)},
    { id: PaymentMethod.peerToPeer, name: this.paymentMethodService.nameof(PaymentMethod.peerToPeer)},
    { id: PaymentMethod.unionPay, name: this.paymentMethodService.nameof(PaymentMethod.unionPay)},
    { id: PaymentMethod.alternativePayment, name: this.paymentMethodService.nameof(PaymentMethod.alternativePayment)},
    { id: PaymentMethod.demoPay, name: this.paymentMethodService.nameof(PaymentMethod.demoPay)}
  ];

  filter: TransactionFilter = {
    field: this.filterFields[0].id,
    value: '',
    recipient: '',
    sender: '',
    cardBin: '',
    userOnly: false,
    paymentMethod: this.filterPaymentMethods[0].id,
    direction: this.filterDirections[0].id,
    startDate: new Date(),
    endDate: new Date()
  };

  requestedPage: number = 1;

  displayedColumns: string[] = ['3ds', 'transactionDate', 'transactionId', 'recipient', 'sender', 'payer', 'amount', 'paymentMethod', 'threeDs', 'action'];

  override ngOnInit() {
    super.ngOnInit();

    this.search();
  }

  get paymentMethod() {
    return PaymentMethod;
  }

  initFilter(period: PeriodFilter) {
    this.periodChange(period);
    this.search();
  }

  periodChange(period: PeriodFilter) {
    this.filter.startDate = period.startDate;
    this.filter.endDate = period.endDate;
    this.search();
  }

  dateChanged() {
    this.filterPeriods.reset();
  }

  get searchAllowed() {
    return !this.filter?.field || (this.filter?.field && this.filter?.value);
  }

  search() {
    if (this.searchAllowed) {
      this.requestedPage = 1;
      this.loadTransactions();
    }
  }

  get pagination() {
    return this.transactions as Pagination;
  }

  pageChanged(page: number) {
    this.requestedPage = page;
    this.loadTransactions();
  }

  loadTransactions() {
    let criteria = this.buildSearchCriteria();
    this.transactionService.lookup(undefined, criteria).subscribe(
      result => { this.transactions = result as PaginatedList<Transaction>; },
      error => { this.showError(error, 'Get transactions has failed due to server error'); }
    );
  }

  buildSearchCriteria() {
    let filter = this.searchFilterBuilderService.build(this.filter, this.filterFields);

    const criteria = {
      since: (this.requestedPage - 1) * this.transactions.pageSize,
      limit: this.transactions.pageSize,
      sort: '-TransactionDate',
      filter: filter ? `AND$${filter}` : filter
    };

    return criteria;
  }

  view(transaction: Transaction) {
    this.router.navigate(['/main/transaction', transaction.id], { queryParams: { consumerId: this.manager.consumerId } });
  }

  export() {
    if (this.searchAllowed) {
      let criteria = this.buildSearchCriteria();
      this.transactionService.export(undefined, criteria).subscribe(
        response => {
          const fileName = `transactions-history-${formatDate(this.filter.startDate, 'dd-MM-yyyy', 'en-US')}-to-${formatDate(this.filter.endDate, 'dd-MM-yyyy', 'en-US')}.xlsx`;
          const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          const downloadLink = document.createElement('a');
          downloadLink.href = URL.createObjectURL(blob);
          downloadLink.download = fileName || '';
          downloadLink.click();
        },
        error => { this.showError(error, 'Export transactions has failed due to server error'); }
      );
    }
  }
}
